/**
 * Outstanding-credit messaging, appended at the end of every scenario.
 * E — credit exceeds pay today; F — credit also covers the next bill.
 */
const creditFragments = (model) => {
    if (!model.credit || model.credit.remaining <= 0)
        return [];
    return model.credit.coversNextBill ? ['E', 'F'] : ['E'];
};
/**
 * The renewal line. Normally the plain "Your subscription will renew at {price}
 * on {date}" (C). When a recurring discount means the next charge later reverts
 * to a higher full price, it becomes the "Thereafter, …{fullPrice} on {date}"
 * variant (C-2) — same slot, picked by whether the full price actually differs.
 */
const renewalFragment = (model) => model.fullPriceRenewal.price !== model.renewal.price ? 'C-2' : 'C';
export const resolveScenario = (model) => {
    // Preserved extra-member multi-month discount offer — self-contained copy
    // that fully replaces the matrix (it even omits the trailing credit line).
    if (model.slots?.offer) {
        return ['extraMemberOffer'];
    }
    // The lock-in copy states a percentage, so it needs one: without a derived
    // percentage the fragment is dropped rather than rendered with a blank.
    const isAnnual = model.hasAnnualDiscount && model.annualDiscountPercentage !== undefined;
    const isNew = model.intent === 'new';
    const onTrial = !!model.trialDaysRemaining && model.trialDaysRemaining > 0;
    // V14 / V15 — adding extra-member slots to an *existing* subscription (a
    // first-time extra member, V13, comes through as a plain `new` purchase).
    const isSlots = !!model.slots && !isNew;
    const fragments = [];
    // 1. Lead sentence — the scenario's opening line.
    // V19
    if (isNew && model.startsTrial)
        fragments.push('trialStart');
    // V1 / V13
    else if (isNew && !model.promo)
        fragments.push('A');
    else if (isNew)
        fragments.push('A-1');
    // V11
    else if (model.intent === 'refund')
        fragments.push('G');
    // V12
    else if (model.intent === 'samePlanRefund')
        fragments.push('H');
    // V14 / V15
    else if (isSlots)
        fragments.push('I');
    // V2 — plan change mid-trial
    else if (onTrial)
        fragments.push('trialPrefix');
    // 2. Promo line (V3 / V6 / V16–V18), with its annual lock-in variant (B-2)
    //    immediately after. Always honoured when present, regardless of intent.
    if (model.promo && model.intent !== 'samePlanRefund') {
        fragments.push('D');
        if (isAnnual)
            fragments.push('B-2');
    }
    // 3. Renewal line (C) + structural annual lock-in (B-1). Skipped where the
    //    lead sentence already states the recurring price: a plain new purchase
    //    (A), a same-plan refund (H) and the slots line (I).
    const hasRenewal = !((isNew && !model.startsTrial && !model.promo) ||
        model.intent === 'samePlanRefund' ||
        isSlots);
    // B-2 already carried the annual lock-in for a promo, so B-1 is promo-free.
    const wantsAnnualLockIn = isAnnual && !model.promo;
    // The lock-in trails the renewal during any trial (new-trial or mid-trial
    // plan change) and leads it for a plain plan change.
    const lockInTrailsRenewal = onTrial || !!model.startsTrial;
    if (hasRenewal) {
        if (wantsAnnualLockIn && !lockInTrailsRenewal && model.intent !== 'refund')
            fragments.push('B-1');
        fragments.push(renewalFragment(model));
        if (wantsAnnualLockIn && lockInTrailsRenewal && model.intent !== 'refund')
            fragments.push('B-1');
    }
    else if (wantsAnnualLockIn && isNew) {
        // Plain new annual purchase: A absorbs the renewal, so B-1 simply follows.
        fragments.push('B-1');
    }
    // 4. Outstanding-credit messaging, appended at the end of every scenario.
    fragments.push(...creditFragments(model));
    return fragments;
};
